// ABOUTME: Applies a pending PWA update automatically on the next route change.
// ABOUTME: Keeps long-lived sessions off stale builds without interrupting the current screen.

import { useEffect, useRef, type FC } from "react"
import { useLocation } from "react-router"
import { hasUnsavedWork } from "@shared/utils/unsavedWork"
import { useUpdateContext } from "./useUpdateContext"

// A route change is the least disruptive moment to swap versions: the user has
// just left the previous screen, so the reload triggered by SW activation lands
// them on the page they were navigating to, with no in-progress edits to lose.
// The manual sidebar indicator stays as-is for users who want to update sooner.
export const AutoUpdateOnNavigate: FC = () => {
  const { isUpdateAvailable, isUpdating, triggerUpdate } = useUpdateContext()
  const { pathname } = useLocation()
  const prevPathnameRef = useRef(pathname)

  useEffect(() => {
    // Only a pathname transition counts as navigation — the effect also
    // re-runs when isUpdateAvailable flips, and updating mid-screen (while
    // the user may be editing) is exactly what this component avoids.
    const pathChanged = pathname !== prevPathnameRef.current
    prevPathnameRef.current = pathname
    // Leaving a screen leaves its edits behind, but not all work: a message written in a discussion outlives its window
    // (messageDrafts) and holds the tab — the reload waits for a navigation with nothing to lose, or the indicator.
    if (pathChanged && isUpdateAvailable && !isUpdating && !hasUnsavedWork()) {
      void triggerUpdate()
    }
  }, [pathname, isUpdateAvailable, isUpdating, triggerUpdate])

  return null
}
